import { useEffect, useState, useMemo, useCallback } from "react";
import { Table, Form, Alert, Input, Flex } from "antd";
import { dealsView, raiseRequest } from "../../../redux/slices/DealSlice";
import { useDispatch, useSelector } from "react-redux";
import { capitalizeWords } from "../../../helpers/helpers";
import CustomButton from "../../../components/CustomButton/Button";
import CustomModal from "../../../components/CustomModal/CustomModal";
import SuccessResultModal from "../../../components/SuccessResultModal/SuccessResultModal";
import { useNavigate } from "react-router-dom";
import { BTN_TXT, TITLE_TXT } from "../../../constants/constants";
import { TABLE_PAGINATION } from "../../../constants/tableConfig";
import { getDealsColumns } from "./getDealsColumns";
import { DEAL_CATEGORY_IDS } from "../../../helpers/dealCategoryProducts";

const DealsTable = ({ selectedStatus, searchEnt, currentDealCategoryId: propDealCategoryId }) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { dealsDataLoading, dealsData, raiseReqError, raiseReqLoading } = useSelector((state) => state?.deals);
    const { activeCategory } = useSelector((state) => state?.dealCategory || {});
    const user = useSelector((state) => state?.auth?.user);

    const currentDealCategoryId =
        propDealCategoryId ||
        activeCategory?.dealCategoryId ||
        user?.dealCategories?.[0]?.dealCategoryId ||
        DEAL_CATEGORY_IDS.INVOICE_FINANCING;

    const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
    const [reasonType, setReasonType] = useState("");
    const [selectedDealId, setSelectedDealId] = useState(null);
    const [successModalOpen, setSuccessModalOpen] = useState(false);

    useEffect(() => {
        dispatch(dealsView({ status: selectedStatus, dealCategoryId: currentDealCategoryId }));
    }, [selectedStatus, currentDealCategoryId, dispatch]);

    const handleCommentsModalOpen = useCallback((dealId, type) => {
        setSelectedDealId(dealId);
        setReasonType(type);
        setIsReasonModalOpen(true);
    }, []);

    const handleSubmitRequest = useCallback(async (values) => {
        const payload = {
            type: reasonType,
            dealId: selectedDealId,
            comments: values.reason,
        };
        const response = await dispatch(raiseRequest(payload));

        if (response?.payload?.statusCode === 201) {
            setIsReasonModalOpen(false);
            form.resetFields();
            setSuccessModalOpen(true);
            setTimeout(() => {
                setSuccessModalOpen(false);
            }, 2000);
        }
    }, [dispatch, form, reasonType, selectedDealId]);

    const handleCloseReasonModal = useCallback(() => {
        setIsReasonModalOpen(false);
    }, []);

    const handleCloseSuccessModal = useCallback(() => {
        setSuccessModalOpen(false);
    }, []);

    const columns = useMemo(
        () =>
            getDealsColumns({
                selectedStatus,
                dealCategoryId: currentDealCategoryId,
                navigate,
                onOpenReasonModal: handleCommentsModalOpen,
            }),
        [selectedStatus, currentDealCategoryId, navigate, handleCommentsModalOpen]
    );

    const filteredData = useMemo(
        () =>
            dealsData?.filter((item) => {
                const matchesSearch = item.entName?.toLowerCase().includes(searchEnt.toLowerCase());
                const itemCat = item.dealCategoryId || DEAL_CATEGORY_IDS.INVOICE_FINANCING;
                const matchesCategory = !currentDealCategoryId || itemCat === currentDealCategoryId;
                return matchesSearch && matchesCategory;
            }) ?? [],
        [dealsData, searchEnt, currentDealCategoryId]
    );

    return (
        <>
            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="dealId"
                loading={dealsDataLoading}
                bordered
                scroll={{ x: "max-content" }}
                pagination={TABLE_PAGINATION}
                className="deals-table"
            />
            <CustomModal
                title={`${capitalizeWords(reasonType)} Request (Add Your Reason)`}
                open={isReasonModalOpen}
                centered
                onCancel={handleCloseReasonModal}
                content={
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmitRequest}
                        initialValues={{ remember: true }}
                        className="reason-form"
                    >
                        <Form.Item
                            label="Reason"
                            name="reason"
                            rules={[{ required: true, message: "Reason cannot be empty!" }]}
                        >
                            <Input.TextArea
                                rows={4}
                                placeholder={`Please provide a brief explanation for ${reasonType}ing this deal.`}
                                maxLength={500}
                            />
                        </Form.Item>
                        {raiseReqError && <Alert style={{ marginBottom: 20 }} message={raiseReqError} type="error" showIcon />}
                        <Form.Item>
                            <Flex justify="flex-end">
                                <CustomButton
                                    loading={raiseReqLoading}
                                    type="primary"
                                    htmlType="submit"
                                    style={{ padding: 24 }}
                                >
                                    {BTN_TXT.SUBMIT}
                                </CustomButton>
                            </Flex>
                        </Form.Item>
                    </Form>
                }
            />
            <SuccessResultModal
                open={successModalOpen}
                onClose={handleCloseSuccessModal}
                title={TITLE_TXT.TQ}
                subTitle={`Your ${capitalizeWords(reasonType)} request has been successfully raised for admin review`}
            />
        </>
    );
};

export default DealsTable;
